import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  LadingResponse,
  DocumentInfo,
  SignatureInfo,
  LadingProductImage,
  LadingProductDeliveryResponse,
  WorkOrderStatusTimelinePayload,
  WorkOrderStatusTimelineResponse
} from './work-order.interfaces';

@Injectable({ providedIn: 'root' })
export class WorkOrderService {
  constructor(private http: HttpClient) {}

  getWorkOrderDetails(uuid: string): Observable<LadingResponse> {
    return this.http.get<LadingResponse>(`/api/work_orders/work_order_details.php?uuid=${uuid}`);
  }

  getDocuments(uuid: string): Observable<DocumentInfo[]> {
    return this.http.get<DocumentInfo[]>(`/api/work_orders/order_documents.php?uuid=${uuid}`);
  }

  getSignatures(uuid: string): Observable<SignatureInfo[]> {
    return this.http.get<SignatureInfo[]>(`/api/work_orders/signature_list.php?uuid=${uuid}`);
  }

  getLadingList(query: string): Observable<string[]> {
    return this.http.get<string[]>(`/api/work_orders/query_ladings.php?type=alternate_reference&search_term=${query}`);
  }

  uploadDocuments(formData: FormData): Observable<any> {
    return this.http.post('api/documents/document_upload.php', formData);
  }

  getProductImages(uuid: string): Observable<any> {
  return this.http.get<any>(`/api/work_orders/lading_product_images.php?lading_uuid=${uuid}`);
}

  getLadingProductDeliveryPreview(ladingUuid: string): Observable<LadingProductDeliveryResponse> {
    return this.http.get<LadingProductDeliveryResponse>('/api/work_orders/lading_product_delivery.php', {
      params: { lading_uuid: ladingUuid }
    });
  }

  markLadingProductsDelivered(
    ladingUuid: string,
    deliveredTimeUtc: string
  ): Observable<LadingProductDeliveryResponse> {
    return this.http.post<LadingProductDeliveryResponse>('/api/work_orders/lading_product_delivery.php', {
      lading_uuid: ladingUuid,
      delivered_time: deliveredTimeUtc
    });
  }

  createStatusTimeline(
    payload: WorkOrderStatusTimelinePayload
  ): Observable<WorkOrderStatusTimelineResponse> {
    return this.http.post<WorkOrderStatusTimelineResponse>(
      '/api/work_orders/work_order_status_timeline.php',
      payload
    );
  }

  updateStatusTimeline(
    payload: WorkOrderStatusTimelinePayload
  ): Observable<WorkOrderStatusTimelineResponse> {
    return this.http.put<WorkOrderStatusTimelineResponse>(
      '/api/work_orders/work_order_status_timeline.php',
      payload
    );
  }

  deleteStatusTimeline(
    ladingUuid: string,
    timelineUuid: string
  ): Observable<WorkOrderStatusTimelineResponse> {
    return this.http.delete<WorkOrderStatusTimelineResponse>(
      '/api/work_orders/work_order_status_timeline.php',
      {
        params: {
          lading_uuid: ladingUuid,
          lading_status_timeline_uuid: timelineUuid
        }
      }
    );
  }

  async getSignatureFileURL(signatureInfo: SignatureInfo): Promise<string> {
    const signatureUrl = `/api/work_orders/signature.php?uuid=${signatureInfo.signature_uuid}`;
    const signatureResponse = await this.http.get<any>(signatureUrl).toPromise();
    const base64 = signatureResponse[0].signature_capture;
    return this.createFileURLFromBase64(base64, `image/${signatureInfo.file_format}`);
  }

  async getDocumentFileURL(fileInfo: DocumentInfo): Promise<string> {
    const base64 = await this.http.get<string>(
      '/api/work_orders/document_content.php',
      {
        params: {
          document_uuid: fileInfo.upload_document_uuid
        }
      }
    ).toPromise();

    if (!base64 || typeof base64 !== 'string') {
      throw new Error('Document content was empty.');
    }

    const format = (fileInfo.file_format || '').toLowerCase();
    let mimeType = 'application/pdf';
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(format)) {
      mimeType = `image/${format === 'jpg' ? 'jpeg' : format}`;
    } else if (format === 'tif' || format === 'tiff') {
      mimeType = 'image/tiff';
    } else if (format !== 'pdf') {
      mimeType = 'application/octet-stream';
    }

    return this.createFileURLFromBase64(base64, mimeType);
  }

  private createFileURLFromBase64(base64: string, mimeType: string): string {
    const byteArray = new Uint8Array(window.atob(base64).split("").map(char => char.charCodeAt(0)));
    const file = new Blob([byteArray], { type: mimeType });
    return URL.createObjectURL(file);
  }
}
